import React, { useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import NavigationBar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AddOrEditPurchase from './pages/AddOrEditPurchase';
import AddOrEditProduct from './pages/AddOrEditProduct';
import AddOrEditCRT from './pages/AddOrEditCRT';
import Purchases from './pages/Purchases';
import Products from './pages/Products';
import Waitlist from './pages/Waitlist';
import ProductWaitlist from './pages/ProductWaitlist';
import Shipping from './pages/Shipping';
import Messages from './pages/Messages';
import Training from './pages/Training';
import Articles from './pages/Articles';
import CRTDatabase from './pages/CRTDatabase';
import CRTAttributes from './pages/CRTAttributes';
import Documents from './pages/Documents';
import CRTDetail from './pages/CRTDetail';
import ModdableCRTs from './pages/ModdableCRTs';
import NotModdableCRTs from './pages/NotModdableCRTs';
import ModBoards from './pages/ModBoards';
import Chat from './pages/Chat';
import LocalizationSettings from './pages/LocalizationSettings';
import { AuthContext } from './contexts/AuthContext';
import Settings from './pages/Settings';
import AddOrEditStore from './pages/AddOrEditStore';
import StripeSettings from './pages/StripeSettings';
import PaypalSettings from './pages/PaypalSettings';
import SuperAdmin from './pages/SuperAdmin';
import OrphanedImagesManager from './pages/OrphanedImagesManager';
import VoltageDividerCalculator from './pages/VoltageDividerCalculator';
import RGBMuxCalculator from './pages/RGBMuxCalculator';
import EbayImageExtractor from './pages/EbayImageExtractor';

function App() {
  const { isAuthenticated, validateToken, role } = useContext(AuthContext);
  const [loading, setLoading] = useState(true); // Loading state to prevent rendering before auth is resolved
  const navigate = useNavigate();
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    // On initial load, get the theme from localStorage if available, otherwise default to light theme
    const storedTheme = localStorage.getItem('theme');
    return storedTheme ? storedTheme === 'dark' : true; // Default to dark theme if no theme is stored
  });

  // --- Sidebar collapse state ---
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsed');
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
    } catch {}
  }, [isCollapsed]);
  // --- End sidebar collapse state ---

  // Effect to validate the token and resolve authentication status
  useEffect(() => {
    const validate = async () => {
      const isValid = await validateToken();
      if (!isValid) {
        console.log('Invalid Token!');
      }
      setLoading(false); // Set loading to false after token is validated
    };

    validate();
  }, [navigate, validateToken]);
  //}, [isAuthenticated, navigate, validateToken]);

  // Toggle theme and store the preference in localStorage
  const toggleTheme = () => {
    setIsDarkTheme(prevTheme => {
      const newTheme = !prevTheme;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light'); // Store the theme in localStorage
      return newTheme;
    });
  };

  // Apply the selected theme to the document body
  useEffect(() => {
    if (isDarkTheme) {
      document.body.setAttribute('data-bs-theme', 'dark');
    } else {
      document.body.setAttribute('data-bs-theme', 'light');
    }
  }, [isDarkTheme]);

  if (loading) {
    return; // Render loading indicator until authentication status is resolved
  }

  return (
    <div className="theme-container">
      {/* Default SEO tags for pages that don't have specific ones */}
      <Helmet>
        <title>Sunthar - Where old circuits spark new ideas!</title>
        <meta name="description" content="A space where retro tech is explored, revived, and reimagined for the modern era. Connect, share, and rediscover the magic of classic technology." />
        <meta name="keywords" content="retro technology, vintage electronics, CRT monitors, RGB modification, circuit design, electronics repair, vintage computing" />
        <meta name="author" content="Sunthar's Super Sector" />
        <link rel="canonical" href="https://sunthar.com" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Sunthar - Where old circuits spark new ideas!" />
        <meta property="og:description" content="A space where retro tech is explored, revived, and reimagined for the modern era. Connect, share, and rediscover the magic of classic technology." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sunthar.com" />
        <meta property="og:image" content="https://sunthar.com/dashboard_background.jpg" />
        <meta property="og:site_name" content="Sunthar's Super Sector" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sunthar - Where old circuits spark new ideas!" />
        <meta name="twitter:description" content="A space where retro tech is explored, revived, and reimagined for the modern era. Connect, share, and rediscover the magic of classic technology." />
        <meta name="twitter:image" content="https://sunthar.com/dashboard_background.jpg" />
        <meta name="twitter:site" content="@SuntharSector" />
      </Helmet>
      
      <NavigationBar toggleTheme={toggleTheme} isDarkTheme={isDarkTheme}/>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div className={`admin-page-wrapper ${isAuthenticated ? 'main-content' : ''} ${isCollapsed ? 'with-sidebar-collapsed' : ''}`}>
        <Routes>
          {/* Take user to homepage */}
          <Route path="/" element={<Home />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/moddable" element={<ModdableCRTs />} />
          <Route path="/not-moddable" element={<NotModdableCRTs />} />
          <Route path="/voltage-divider-calculator" element={<VoltageDividerCalculator />} />
          <Route path="/rgb-mux-calculator" element={<RGBMuxCalculator />} />
          <Route path="/crts/:slug" element={<CRTDetail />} />
          {isAuthenticated ? (
            <>
              <Route path="/purchases" element={<Purchases />} />
              <Route path="/add-purchase/:store_id" element={<AddOrEditPurchase isEdit={false} />} />
              <Route path="/edit-purchase/:id" element={<AddOrEditPurchase isEdit={true} />} />
              <Route path="/products" element={<Products defaultType="sale"/>} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/showcase" element={<Products defaultType="showcase" />} />
              <Route path="/services" element={<Products defaultType="service" />} />
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/waitlist/:product_id" element={<ProductWaitlist />} />
              <Route path="/add-product" element={<AddOrEditProduct isEdit={false} defaultType="sale" />} />
              <Route path="/add-showcase" element={<AddOrEditProduct isEdit={false} defaultType="showcase" />} />
              <Route path="/add-service" element={<AddOrEditProduct isEdit={false} defaultType="service" />} />
              <Route path="/edit-product/:id" element={<AddOrEditProduct isEdit={true} defaultType="sale" />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/add-store" element={<AddOrEditStore />} />
              <Route path="/settings/edit-store/:storeId" element={<AddOrEditStore />} />
              <Route path="/settings/stripe" element={<StripeSettings />} />
              <Route path="/settings/paypal" element={<PaypalSettings />} />
              <Route path="/settings/shipping" element={<Shipping />} />
              <Route path="/settings/messages" element={<Messages />} />
              <Route path="/settings/training" element={<Training />} />
              <Route path="/localization" element={<LocalizationSettings />} />
              {role === 'super' && (
                <>
                  <Route path="/superadmin" element={<SuperAdmin />} />
                  <Route path="/superadmin/crts" element={<CRTDatabase />} />
                  <Route path="/superadmin/crtattributes" element={<CRTAttributes />} />
                  <Route path="/superadmin/documents" element={<Documents />} />
                  <Route path="/superadmin/add-crt" element={<AddOrEditCRT isEdit={false} />} />
                  <Route path="/superadmin/edit-crt/:id" element={<AddOrEditCRT isEdit={true} />} />
                  <Route path="/superadmin/mod-boards" element={<ModBoards />} />
                  <Route path="/superadmin/orphaned-images" element={<OrphanedImagesManager />} />
                  <Route path="/superadmin/ebay-image-extractor" element={<EbayImageExtractor />} />
                </>
              )}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="*" element={<Navigate to="/" replace />} /> // Redirect to home if not authenticated
            </>
          )}
        </Routes>
      </div>
      <div className={`${isAuthenticated ? 'main-content' : ''}`}>
        <Footer />
      </div>
    </div>
  );
}

export default App;
